import React, { useState } from "react";
import { calculateCustomHandicap } from "./HandicapService";

export default function QuickRound({
  user,
  courses = [],
  playerDirectory = [],
  roundHistory = [],
  onSave,
  onBack,
}) {
  const [roundDate, setRoundDate] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [players, setPlayers] = useState([]);
  const [scores, setScores] = useState({});
  const [entryMode, setEntryMode] = useState("total"); // "total" or "holeByHole"
  const [holeScores, setHoleScores] = useState({});

  const addPlayer = (id) => {
    const profile = playerDirectory.find((p) => p.id === id);
    if (profile && !players.find((p) => p.id === id) && players.length < 4) {
      setPlayers([...players, profile]);
    }
  };

  const removePlayer = (id) => {
    setPlayers(players.filter((p) => p.id !== id));
    setScores((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setHoleScores((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const setHoleScore = (playerId, holeNum, val) => {
    setHoleScores((prev) => ({
      ...prev,
      [playerId]: {
        ...(prev[playerId] || {}),
        [holeNum]: val === "" ? "" : parseInt(val, 10),
      },
    }));
  };

  const handleSave = () => {
    if (!selectedCourse) { alert("Please select a course."); return; }
    if (players.length === 0) { alert("Please add at least one player."); return; }

    let scoresObj = {};

    if (entryMode === "total") {
      const missing = players.filter((p) => !scores[p.id] || Number(scores[p.id]) === 0);
      if (missing.length > 0) {
        alert(`Missing score for: ${missing.map((p) => p.full_name).join(", ")}`);
        return;
      }
      players.forEach((p) => {
        scoresObj[p.id] = { total: Number(scores[p.id]) };
      });
    } else {
      scoresObj = players.reduce((acc, player) => {
        acc[player.id] = holeScores[player.id] || {};
        return acc;
      }, {});
    }

    const handicapsObj = {};
    players.forEach((p) => {
      const priorRounds = roundHistory.filter(r => new Date(r.date) < new Date(roundDate));
      const hcpResult = priorRounds.length > 0
        ? calculateCustomHandicap(priorRounds, p.initial_handicap || 4, p.full_name)
        : { current: p.initial_handicap || 4 };
      handicapsObj[p.id] = hcpResult.current;
    });

    const round = {
      user_id: user.id,
      date: roundDate,
      course_id: selectedCourse.id,
      course_name: selectedCourse.name,
      course_data: selectedCourse,
      players: players.map((p) => p.id),
      scores: scoresObj,
      player_handicaps: handicapsObj,
      selected_games: ["Stroke Play"],
      is_finalized: true,
      is_quick_entry: entryMode === "total",
    };

    onSave(round);
  };

  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    fontSize: "1rem",
    background: "#fff",
    boxSizing: "border-box",
  };

  const cardStyle = {
    background: "#fff",
    borderRadius: "16px",
    padding: "18px",
    marginBottom: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  };

  const labelStyle = {
    display: "block",
    fontSize: "0.7rem",
    fontWeight: "800",
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "8px",
  };

  const holeInputStyle = {
    width: "100%",
    padding: "6px 2px",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    fontSize: "0.9rem",
    fontWeight: "700",
    textAlign: "center",
    background: "#fff",
    boxSizing: "border-box",
    MozAppearance: "textfield",
    WebkitAppearance: "none",
    appearance: "none",
  };

  const renderHoleGrid = (player, startHole, endHole, label) => (
    <div style={{ marginBottom: "12px" }}>
      <div style={{
        fontSize: "0.65rem", fontWeight: "800", color: "#94a3b8",
        textTransform: "uppercase", letterSpacing: "0.05em",
        marginBottom: "6px",
      }}>
        {label}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(9, 1fr)", gap: "4px" }}>
        {Array.from({ length: endHole - startHole + 1 }, (_, i) => startHole + i).map((holeNum) => {
          const hole = selectedCourse?.holes?.[holeNum - 1];
          return (
            <div key={holeNum} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
              <span style={{ fontSize: "0.6rem", fontWeight: "700", color: "#64748b" }}>{holeNum}</span>
              <input
                type="number"
                min="1"
                max="15"
                value={holeScores[player.id]?.[holeNum] ?? ""}
                onChange={(e) => setHoleScore(player.id, holeNum, e.target.value)}
                style={holeInputStyle}
              />
              {hole && (
                <span style={{ fontSize: "0.55rem", color: "#94a3b8", fontWeight: "600" }}>
                  {hole.par}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={{ backgroundColor: "#f4f4f5", minHeight: "100vh", paddingBottom: "40px" }}>
      {/* Header */}
      <div
        style={{
          background: "#fff",
          padding: "15px 20px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          borderBottom: "1px solid #e2e8f0",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            fontSize: "1.5rem",
            color: "#94a3b8",
            cursor: "pointer",
            lineHeight: 1,
            padding: 0,
          }}
        >
          ←
        </button>
        <h2 style={{ margin: 0, color: "#1b4332", fontSize: "1.2rem" }}>Quick Round Entry</h2>
      </div>

      <div style={{ padding: "16px" }}>
        {/* Date */}
        <div style={cardStyle}>
          <label style={labelStyle}>Round Date</label>
          <input
            type="date"
            value={roundDate}
            onChange={(e) => setRoundDate(e.target.value)}
            style={inputStyle}
          />
          <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: "4px" }}>
            Required — please select the date the round was played
          </div>
        </div>

        {/* Course */}
        <div style={cardStyle}>
          <label style={labelStyle}>Course</label>
          <select
            value={selectedCourse?.id || ""}
            onChange={(e) => setSelectedCourse(courses.find((c) => c.id === e.target.value) || null)}
            style={inputStyle}
          >
            <option value="">— Select a course —</option>
            {[...courses]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
          </select>
        </div>

        {/* Players */}
        <div style={cardStyle}>
          <label style={labelStyle}>Players ({players.length}/4)</label>
          <select
            value=""
            onChange={(e) => { addPlayer(e.target.value); e.target.value = ""; }}
            style={inputStyle}
            disabled={players.length >= 4}
          >
            <option value="">— Add a player —</option>
            {playerDirectory
              .filter((p) => !players.find((sel) => sel.id === p.id))
              .sort((a, b) => a.full_name.localeCompare(b.full_name))
              .map((p) => (
                <option key={p.id} value={p.id}>{p.full_name}</option>
              ))}
          </select>
          {players.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
              {players.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 12px",
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "20px",
                    fontSize: "0.85rem",
                    color: "#1b4332",
                    fontWeight: "600",
                  }}
                >
                  {p.full_name.split(" ")[0]}
                  <span
                    onClick={() => removePlayer(p.id)}
                    style={{ cursor: "pointer", color: "#f87171", fontWeight: "700" }}
                  >
                    ✕
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Entry Mode Toggle */}
        {players.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            <label style={labelStyle}>Entry Mode</label>
            <div style={{ display: "flex", background: "#f1f5f9", borderRadius: "10px", padding: "4px" }}>
              <button
                onClick={() => setEntryMode("total")}
                style={{
                  flex: 1, padding: "10px", border: "none", borderRadius: "8px",
                  background: entryMode === "total" ? "#1b4332" : "transparent",
                  color: entryMode === "total" ? "white" : "#64748b",
                  fontWeight: "700", fontSize: "0.8rem", cursor: "pointer",
                }}
              >
                Total Score
              </button>
              <button
                onClick={() => setEntryMode("holeByHole")}
                style={{
                  flex: 1, padding: "10px", border: "none", borderRadius: "8px",
                  background: entryMode === "holeByHole" ? "#1b4332" : "transparent",
                  color: entryMode === "holeByHole" ? "white" : "#64748b",
                  fontWeight: "700", fontSize: "0.8rem", cursor: "pointer",
                }}
              >
                Hole by Hole
              </button>
            </div>
          </div>
        )}

        {/* Total Score Inputs */}
        {players.length > 0 && entryMode === "total" && (
          <div style={cardStyle}>
            <label style={labelStyle}>Total Scores</label>
            {players.map((p) => (
              <div
                key={p.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                  gap: "12px",
                }}
              >
                <span style={{ fontWeight: "700", color: "#1b4332", flex: 1 }}>
                  {p.full_name}
                </span>
                <input
                  type="number"
                  min="18"
                  max="200"
                  placeholder="Score"
                  value={scores[p.id] || ""}
                  onChange={(e) => setScores({ ...scores, [p.id]: e.target.value })}
                  style={{
                    width: "90px",
                    padding: "10px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    fontSize: "1.1rem",
                    fontWeight: "700",
                    textAlign: "center",
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Hole-by-Hole Grid */}
        {players.length > 0 && entryMode === "holeByHole" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {players.map((player) => (
              <div
                key={player.id}
                style={{
                  background: "#fff",
                  borderRadius: "16px",
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <div style={{
                  background: "#1b4332",
                  padding: "12px 16px",
                  color: "white",
                  fontWeight: "800",
                  fontSize: "0.95rem",
                }}>
                  {player.full_name}
                </div>
                <div style={{ padding: "14px" }}>
                  {renderHoleGrid(player, 1, 9, "Front 9")}
                  <div style={{
                    borderTop: "1px solid #f1f5f9",
                    marginBottom: "12px",
                  }} />
                  {renderHoleGrid(player, 10, 18, "Back 9")}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={!roundDate || !selectedCourse || players.length === 0}
          style={{
            width: "100%",
            padding: "16px",
            marginTop: "14px",
            backgroundColor: !roundDate || !selectedCourse || players.length === 0 ? "#94a3b8" : "#1b4332",
            color: "white",
            border: "none",
            borderRadius: "12px",
            fontWeight: "800",
            fontSize: "1rem",
            cursor: !roundDate || !selectedCourse || players.length === 0 ? "not-allowed" : "pointer",
            boxShadow: "0 4px 12px rgba(27,67,50,0.2)",
          }}
        >
          Save Round
        </button>
        <button
          onClick={onBack}
          style={{
            width: "100%",
            marginTop: "12px",
            background: "none",
            border: "none",
            color: "#ef4444",
            fontSize: "0.85rem",
            fontWeight: "600",
            cursor: "pointer",
            padding: "10px"
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
